import { inject, injectable } from "inversify";
import {
  HttpAddressSearchResponse,
  HttpCoordinateResponse,
  HttpUtmkCoordinateResponse,
  IAddressRepository,
  IAddressSearchInfoCollectionParser,
  ICoordinateInfoParser,
  IUtmkCoordinateInfoParser,
} from "./addressRepository.types";
import { IHttpService } from "../shared/httpService";
import Symbols from "@/ioc/identifiers";
import {
  IAddressSearchInfoCollection,
  ICoordinateInfo,
  IUtmkCoordinateInfo,
} from "@/domain/entities/address";
import { Result } from "neverthrow";
import { HttpError } from "../shared/errors/httpError";
import { ParseError } from "../shared/errors/parseError";
import useGetProtocolHost from "@/use/useGetProtocolHost";

@injectable()
export class AddressRepository implements IAddressRepository {
  @inject(Symbols.IAddressSearchInfoCollectionParser)
  private addressSearchInfoCollectionParser!: IAddressSearchInfoCollectionParser;

  @inject(Symbols.IUtmkCoordinateInfoParser)
  private utmkCoordinateInfoParser!: IUtmkCoordinateInfoParser;

  @inject(Symbols.ICoordinateInfoParser)
  private coordinateInfoParser!: ICoordinateInfoParser;

  private httpService: IHttpService;

  constructor(@inject(Symbols.IHttpService) httpService: IHttpService) {
    this.httpService = httpService;
    this.httpService.initService(
      location.hostname === "localhost"
        ? `${location.origin}/addrlink`
        : `${process.env.VUE_APP_JUSO_API_URL}/addrlink`
    );
  }

  async searchAddress(
    searchKeyword: string,
    currentPage: number,
    countPerPage: number
  ): Promise<Result<IAddressSearchInfoCollection, ParseError | HttpError>> {
    const parseTo = (response: HttpAddressSearchResponse) => {
      return this.addressSearchInfoCollectionParser.toDomain(response);
    };

    const params = {
      confmKey: process.env.VUE_APP_JUSO_SEARCH_API_KEY,
      keyword: searchKeyword,
      currentPage: currentPage,
      countPerPage: countPerPage,
      resultType: "json",
    };

    return await this.httpService.get<
      HttpAddressSearchResponse,
      IAddressSearchInfoCollection
    >(
      {
        url: "/addrLinkApi.do",
        config: {
          params,
          withCredentials: false,
        },
      },
      { parseTo }
    );
  }

  async getAddressCoordinate(
    dongNo: string,
    roadNo: string,
    isUnder: boolean,
    buildingMainNo: number,
    buildingSubNo: number
  ): Promise<Result<IUtmkCoordinateInfo, ParseError | HttpError>> {
    const parseTo = (response: HttpUtmkCoordinateResponse) => {
      return this.utmkCoordinateInfoParser.toDomain(response);
    };

    const params = {
      confmKey: process.env.VUE_APP_JUSO_SELECT_API_KEY,
      admCd: dongNo, // 행정구역코드
      rnMgtSn: roadNo, // 도로명코드
      udrtYn: isUnder ? "1" : "0", // 지하여부(0:지상, 1:지하)
      buldMnnm: buildingMainNo, // 건물본번
      buldSlno: buildingSubNo, // 건물부번
      resultType: "json",
    };

    return await this.httpService.get<
      HttpUtmkCoordinateResponse,
      IUtmkCoordinateInfo
    >(
      {
        url: "/addrCoordApi.do",
        config: {
          params,
          withCredentials: false,
        },
      },
      { parseTo }
    );
  }

  async convertUtmkToLatLng(
    utmkX: string,
    utmkY: string
  ): Promise<Result<ICoordinateInfo, ParseError | HttpError>> {
    const parseTo = (response: HttpCoordinateResponse) => {
      return this.coordinateInfoParser.toDomain(response.data);
    };

    const params = {
      x: utmkX,
      y: utmkY,
    };

    return await this.httpService.get<HttpCoordinateResponse, ICoordinateInfo>(
      {
        url: "/convert/utmk/latlng",
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/public/utility`,
          params,
        },
      },
      { parseTo }
    );
  }
}
